import { z } from 'zod';
import { ExareStatus } from '@websky/graphql';
import { CardType } from '../PromoLoader/types';
import { PromoMessageIcons } from '../PromoMessage/types';
import { BooleanOptional, NumberOptional, StringOptional } from './schemaTypes';
import { Sorting } from '../Results/enums';
import { CheckoutSteps } from '../MobileStepbar/types';
import { CheckinStep } from '../Modules/Checkin/types';
import { StepsEnum } from '../types';

const RefundSuccessModalStatusesSchema = z.record(z.nativeEnum(ExareStatus), z.boolean()).optional();

const RefundSchema = z
	.object({
		successModal: z
			.object({
				create: RefundSuccessModalStatusesSchema,
				request: RefundSuccessModalStatusesSchema
			})
			.optional()
	})
	.optional();

export const StepSchema = z.object({
	isHidden: z.boolean().optional(),
	isDisabled: z.boolean().optional(),
	label: z.string(),
	type: z.union([z.nativeEnum(CheckoutSteps), z.nativeEnum(CheckinStep)]).optional(),
	alwaysActive: z.boolean().optional(),
	alwaysOpen: z.boolean().optional(),
	onClick: z.function().args().returns(z.void()).optional()
});

export const StepsSchema = z.record(StepSchema);

const CardSchema = z.object({
	city: StringOptional,
	type: z.nativeEnum(CardType).nullish(),
	title: StringOptional,
	text: StringOptional,
	image: z.string()
});

const PromoMessageLinkSchema = z.object({
	text: z.string(),
	url: z.string().url()
});

export const PromoMessageSchema = z.object({
	title: z.string(),
	content: z.string(),
	icon: z.nativeEnum(PromoMessageIcons).optional(),
	link: PromoMessageLinkSchema.optional()
});

export const EngineSchema = z.object({
	defaultSteps: StepsSchema.optional().describe(
		'@deprecated Задает конфиг шагов (Багаж, Питание, и т.д.) заказа. С помощью этой настройки можно изменить порядок или скрыть шаги. Работает только в результатах поиска'
	),
	servicesStepsOrder: z.array(z.nativeEnum(StepsEnum)).optional(),
	searchFormURL: StringOptional.describe('Адрес страницы с формой поиска (входная точка старта поиска билетов)'),
	promoLoaders: z
		.array(CardSchema)
		.optional()
		.describe('Задает рекламные сообщения, отображающиеся на странице загрузке'),
	simpleLoaders: z.array(CardSchema).optional(),
	promoMessages: z
		.array(PromoMessageSchema)
		.optional()
		.describe(
			'Задает уведомления, которые будут отображаться на странице результатов и странице ввода пассажирских данных'
		),
	checkInEnabled: BooleanOptional.describe('Доступность модуля Онлайн регистрации'),
	checkInURL: StringOptional.describe('Адрес страницы с модулем Онлайн регистрации'),
	showRefundRedirectButton: BooleanOptional.describe('@deprecated'),
	mealWarning: StringOptional.describe('Задает уведомление на странице Питания'),
	mealDefaultFilterButtonPlacement: z
		.enum(['start', 'end'])
		.optional()
		.describe('WEBSKY-3950: Порядок отображения фильтра "Все блюда" на странице питания'),
	calendarDaysCount: NumberOptional.describe('Количество дней в недельном календаре на странице выбора перелетов'),
	showAvailableFaresOnly: BooleanOptional.describe('Отображать только доступные тарифы на выборе перелетов'),
	showTicketRefundOptionModal: BooleanOptional.describe(
		'Отображать модальное окно с предложением услуги возврата полной стоимости билета'
	),
	accountURL: StringOptional,
	nasServiceUpsaleForConfirmedOrder: BooleanOptional,
	sortResultPreferCurrentAirline: BooleanOptional,
	resultsFareSelectorInModal: BooleanOptional,
	resultsFareSlidesToShow: z
		.object({
			desktop: NumberOptional,
			tablet: NumberOptional,
			mobile: NumberOptional
		})
		.optional(),
	ignoreBlockedPaymentToReturnOrder: BooleanOptional.describe(
		'Игнорировать параметр isBlockedForPayment из заказа для отображения кнопки "Изменить заказ"'
	),
	googleOneTapSignInClientId: StringOptional.describe(
		'WEBSKY-5439\nClientId приложения из гугл консоли. Если значение не null, пользователю будет отображаться попап google one tap'
	),
	exareShowPassengersStep: BooleanOptional.describe('Отображать шаг выбора пассажиров в обменах/возвратах'),
	checkFfpStatus: BooleanOptional.describe(
		'В бронировании на шаге с пассажирами, перед переходом на следующий шаг, вызывает запрос CheckFfpStatus для проверки карты лояльности'
	),
	resultsDefaultSorting: z
		.nativeEnum(Sorting)
		.optional()
		.describe('Определяет дефолтное значение сортировки результатов поиска рейсов'),
	nas: z.optional(
		z.object({
			suggestServicesTypes: z.array(z.string()),
			suggestedServicesOrder: z.record(z.number())
		})
	),
	RTMinPricesForOWFlights: BooleanOptional,
	useActualizedOrderFlightPrices: BooleanOptional.describe(
		'После выбора перелетов берем цены из ActualizedOrder вместо SearchResults'
	),
	refund: RefundSchema,
	Exare: z
		.object({
			provideConfirmDocuments: BooleanOptional.describe(
				'показывать переключатель и загрузку подтверждающих документов на странице изменения данных пассажира'
			)
		})
		.optional()
		.describe('настройки Exare для изменения данных пассажира')
});
